/**
 * AuthGuard - Route Protection for All Authenticated Pages
 *
 * Wraps all non-landing-page routes to enforce authentication.
 * Any user who is not signed in or whose 90-day session has expired
 * is redirected to the landing page (/) to sign in again.
 *
 * This is the baseline gate for the entire app. Tech users also have
 * the parallel TechAuthGuard (90-day re-auth) layered on top.
 *
 * Behavior:
 * 1. No auth at all → redirect to / (landing page)
 * 2. Session expired (>90 days) → clear session, redirect to /
 * 3. Stale session (localStorage OK but Firebase Auth has no user, e.g.
 *    revoked token on a long-idle PC) → after a short grace period (a
 *    sign-in in another tab takes ~1s to reach this tab's Firebase Auth),
 *    clear session, redirect to /
 * 4. Auth valid → render children via <Outlet />
 *
 * The stale-session check waits for Firebase Auth to settle (first
 * `onAuthStateChanged` event) before deciding — otherwise we'd redirect
 * during the brief window where Firebase is restoring its session from
 * IndexedDB.
 */

import { useEffect } from 'react';
import { useNavigate, Outlet } from 'react-router-dom';
import {
  useAuth,
  clearAllAuth,
  isSessionStaleNow,
  STALE_SESSION_GRACE_MS,
} from '@/hooks/useAuth';

export function AuthGuard() {
  const navigate = useNavigate();
  const { isAuthenticated, isExpired, isStale, isAuthChecking } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) {
      console.warn('[AuthGuard] No valid session, redirecting to login');
      navigate('/', { replace: true });
      return;
    }

    if (isExpired) {
      console.warn('[AuthGuard] Session expired (90-day limit), redirecting to login');
      clearAllAuth();
      navigate('/', { replace: true });
      return;
    }

    if (!isAuthChecking && isStale) {
      // Wait before clearing — another tab may have JUST signed in and
      // Firebase hasn't synced its user to this tab yet (see
      // STALE_SESSION_GRACE_MS). If Firebase catches up, isStale flips and
      // this timer is cancelled by the effect cleanup.
      const timer = setTimeout(() => {
        if (!isSessionStaleNow()) return;
        console.warn(
          '[AuthGuard] Stale session — localStorage authenticated but Firebase Auth missing. Forcing re-login.'
        );
        clearAllAuth();
        navigate('/', { replace: true });
      }, STALE_SESSION_GRACE_MS);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, isExpired, isStale, isAuthChecking, navigate]);

  // Don't render anything while redirecting OR while Firebase Auth is still
  // settling — rendering the app prematurely would let Firestore listeners
  // fire with no auth token, producing the permission-denied spam we're
  // trying to avoid.
  if (!isAuthenticated || isExpired) {
    return null;
  }

  if (isAuthChecking || isStale) {
    return null;
  }

  return <Outlet />;
}
